import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Plus, Building2, Trash2, Pencil, ArrowLeft, Upload, Download, FileSpreadsheet,
  Loader2, ShieldCheck, Lock, User
} from 'lucide-react';
import { CompanyInfo, CompanyRole, STORAGE_KEYS } from '@/types';
import { saveToLocalStorage, loadFromLocalStorage, generateId, getUserId } from '@/utils/storage';
import { isAdminSession, adminLogin, adminLogout } from '@/utils/auth';
import * as XLSX from 'xlsx';

export default function CompanyManager() {
  const [companies, setCompanies] = useState<CompanyInfo[]>(() => {
    return loadFromLocalStorage<CompanyInfo[]>(STORAGE_KEYS.COMPANIES) || [];
  });
  const [showForm, setShowForm] = useState(false);
  const [editingCompany, setEditingCompany] = useState<CompanyInfo | null>(null);
  const [formData, setFormData] = useState<Partial<CompanyInfo>>({ role: 'partyA' });
  const [showImport, setShowImport] = useState(false);
  const [importing, setImporting] = useState(false);
  const [isAdmin, setIsAdmin] = useState(() => isAdminSession());
  const [activeTab, setActiveTab] = useState<CompanyRole>('partyA');

  // 当前可见的公司列表（根据 Tab 过滤）
  const visibleCompanies = companies.filter(c => {
    if (activeTab === 'partyA') return c.role === 'partyA' || c.role === 'both';
    return c.role === 'partyB' || c.role === 'both';
  });

  // 权限判断：员工只能操作自己创建的（createdBy === 'user' 且 creatorId 匹配）
  const currentUserId = getUserId();
  const canEditOrDelete = (company: CompanyInfo) => {
    if (isAdmin) return true;
    return company.createdBy === 'user' && company.creatorId === currentUserId;
  };

  // 保存公司列表
  const saveCompanies = useCallback((newCompanies: CompanyInfo[]) => {
    setCompanies(newCompanies);
    saveToLocalStorage(STORAGE_KEYS.COMPANIES, newCompanies);
  }, []);

  const handleInputChange = (field: keyof CompanyInfo, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // 新增/编辑公司
  const handleSave = () => {
    if (!formData.fullName?.trim()) {
      alert('公司全称是必填项');
      return;
    }

    const role = (formData.role || 'partyA') as CompanyRole;
    // 普通用户只能创建 partyA / partyB（乙方库对所有用户开放）
    const finalRole = role;

    if (editingCompany) {
      // 编辑时保留原始 createdBy，不覆盖
      const newCompanies = companies.map(c =>
        c.id === editingCompany.id
          ? {
              ...c,
              ...formData,
              role: finalRole,
              updatedAt: new Date().toISOString(),
            } as CompanyInfo
          : c
      );
      saveCompanies(newCompanies);
    } else {
      const newCompany: CompanyInfo = {
        id: generateId(),
        fullName: formData.fullName || '',
        creditCode: formData.creditCode || '',
        legalRepresentative: formData.legalRepresentative || '',
        registeredAddress: formData.registeredAddress || '',
        officeAddress: formData.officeAddress || '',
        phone: formData.phone || '',
        fax: formData.fax || '',
        email: formData.email || '',
        bankName: formData.bankName || '',
        bankAccount: formData.bankAccount || '',
        taxId: formData.taxId || '',
        role: finalRole,
        createdBy: isAdmin ? 'admin' : 'user',
        creatorId: isAdmin ? undefined : getUserId(),
        updatedAt: new Date().toISOString(),
      };
      saveCompanies([...companies, newCompany]);
    }

    setShowForm(false);
    setEditingCompany(null);
    setFormData({ role: activeTab });
  };

  const handleEdit = (company: CompanyInfo) => {
    if (!canEditOrDelete(company)) {
      alert('您只能编辑自己创建的公司信息');
      return;
    }
    setEditingCompany(company);
    setFormData(company);
    setShowForm(true);
  };

  const handleDelete = (id: string, name: string) => {
    const company = companies.find(c => c.id === id);
    if (!company) return;
    if (!canEditOrDelete(company)) {
      alert('您只能删除自己创建的公司信息');
      return;
    }
    if (!window.confirm(`确定要删除公司 "${name}" 吗？此操作不可撤销。`)) return;
    saveCompanies(companies.filter(c => c.id !== id));
  };

  // 导出Excel模板
  const handleExportTemplate = () => {
    const wsData = [
      ['公司全称（必填）', '统一社会信用代码', '法定代表人', '注册地址', '办公地址', '联系电话', '传真号码', '电子邮箱', '开户银行', '银行账号', '纳税人识别号', '角色(partyA/partyB/both)'],
      ['示例公司有限公司', '91110000XXXXXXXX', '张三', '北京市朝阳区XX路XX号', '北京市朝阳区XX路XX号', '010-12345678', '010-87654321', 'contact@example.com', '中国工商银行北京XX支行', '6222021234567890123', '91110000XXXXXXXX', 'partyA'],
    ];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '公司信息模板');
    XLSX.writeFile(wb, '公司信息导入模板.xlsx');
  };

  // 导入Excel
  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImporting(true);
    try {
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data);
      const ws = wb.Sheets[wb.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json<any>(ws, { header: 1 });

      const rows = jsonData.slice(1);
      const newCompanies: CompanyInfo[] = [...companies];
      let count = 0;

      rows.forEach((row: any[]) => {
        if (!row[0] || String(row[0]).trim() === '') return;

        const role = (String(row[11] || 'partyA').trim() as CompanyRole);
        // 普通用户也可导入 partyB（乙方库对所有用户开放）
        const finalRole = role;

        const company: CompanyInfo = {
          id: generateId(),
          fullName: String(row[0] || ''),
          creditCode: String(row[1] || ''),
          legalRepresentative: String(row[2] || ''),
          registeredAddress: String(row[3] || ''),
          officeAddress: String(row[4] || ''),
          phone: String(row[5] || ''),
          fax: String(row[6] || ''),
          email: String(row[7] || ''),
          bankName: String(row[8] || ''),
          bankAccount: String(row[9] || ''),
          taxId: String(row[10] || ''),
          role: finalRole,
          createdBy: isAdmin ? 'admin' : 'user',
          updatedAt: new Date().toISOString(),
        };
        newCompanies.push(company);
        count++;
      });

      saveCompanies(newCompanies);
      alert(`成功导入 ${count} 条公司信息`);
    } catch (error) {
      console.error('导入失败:', error);
      alert('导入失败，请检查文件格式');
    } finally {
      setImporting(false);
      setShowImport(false);
    }
  };

  const handleClearAll = () => {
    if (!window.confirm('确定要清除当前 Tab 下的所有公司信息吗？此操作不可撤销。')) return;
    const remaining = companies.filter(c => {
      if (activeTab === 'partyA') return c.role === 'partyB';
      return c.role === 'partyA';
    });
    saveCompanies(remaining);
  };

  // Tab 标签配置
  const tabs = [
    { key: 'partyA' as CompanyRole, label: '我方公司（甲方）', icon: User },
    { key: 'partyB' as CompanyRole, label: '乙方公司库', icon: Building2 },
  ];

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/" className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <h1 className="text-xl font-bold text-gray-800">公司信息管理</h1>
          <span className="text-sm text-gray-400">共 {companies.length} 个主体</span>
          {isAdmin && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700">
              <ShieldCheck className="w-3 h-3" />
              管理员
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isAdmin ? (
            <button
              onClick={() => { adminLogout(); setIsAdmin(false); }}
              className="text-sm text-gray-500 hover:text-gray-700 border border-gray-300 px-3 py-1.5 rounded-lg"
            >
              退出管理
            </button>
          ) : (
            <button
              onClick={() => {
                const pwd = prompt('请输入管理员密码：');
                if (pwd === null) return;
                if (adminLogin(pwd)) {
                  setIsAdmin(true);
                  alert('管理员登录成功！');
                } else {
                  alert('密码错误！');
                }
              }}
              className="text-sm text-gray-500 hover:text-gray-700 border border-gray-300 px-3 py-1.5 rounded-lg"
            >
              管理员登录
            </button>
          )}
          <button
            onClick={handleExportTemplate}
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 text-sm"
          >
            <Download className="w-4 h-4" />
            下载模板
          </button>
          <button
            onClick={() => setShowImport(true)}
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 text-sm"
          >
            <Upload className="w-4 h-4" />
            批量导入
          </button>
          <button
            onClick={() => {
              setEditingCompany(null);
              setFormData({ role: activeTab });
              setShowForm(true);
            }}
            className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition-colors text-sm"
          >
            <Plus className="w-4 h-4" />
            添加公司
          </button>
        </div>
      </div>

      {/* Tab 切换（乙方库对所有用户可见） */}
      <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 w-fit">
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === tab.key
                  ? 'bg-white text-gray-800 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* 导入弹窗 */}
      {showImport && (
        <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl">
            <h3 className="text-lg font-bold text-gray-800 mb-4">批量导入公司信息</h3>
            <p className="text-sm text-gray-600 mb-4">
              请先下载Excel模板，按格式填写后上传。第12列为角色（partyA/partyB/both）。
            </p>
            <div className="mb-4">
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleImport}
                className="hidden"
                id="import-company-file"
              />
              <label
                htmlFor="import-company-file"
                className="flex items-center justify-center gap-2 w-full p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary hover:bg-primary/5 cursor-pointer transition-colors"
              >
                {importing ? (
                  <Loader2 className="w-5 h-5 animate-spin text-primary" />
                ) : (
                  <FileSpreadsheet className="w-5 h-5 text-gray-400" />
                )}
                <span className="text-sm text-gray-600">
                  {importing ? '导入中...' : '点击选择Excel文件'}
                </span>
              </label>
            </div>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setShowImport(false)}
                className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 text-sm"
              >
                取消
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 新增/编辑表单 */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl shadow-xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-bold text-gray-800 mb-4">
              {editingCompany ? '编辑公司信息' : '添加新公司'}
              {editingCompany && (editingCompany.createdBy === 'admin' || editingCompany.createdBy === 'system') && (
                <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700">
                  <Lock className="w-3 h-3" />
                  管理员添加
                </span>
              )}
            </h3>
            <div className="space-y-4">
              {/* 角色选择（管理员可选） */}
              {isAdmin && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    公司角色
                  </label>
                  <select
                    value={formData.role || 'partyA'}
                    onChange={(e) => handleInputChange('role', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
                  >
                    <option value="partyA">我方公司（甲方）</option>
                    <option value="partyB">乙方公司</option>
                    <option value="both">通用（甲乙均可）</option>
                  </select>
                  <p className="text-xs text-gray-400 mt-1">
                    甲方：仅可选为我方公司 · 乙方：仅可选为对方公司 · 通用：两方均可选
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  公司全称 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.fullName || ''}
                  onChange={(e) => handleInputChange('fullName', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
                  placeholder="请输入公司全称"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { field: 'creditCode', label: '统一社会信用代码', placeholder: '91110000...' },
                  { field: 'legalRepresentative', label: '法定代表人', placeholder: '请输入法定代表人' },
                  { field: 'registeredAddress', label: '注册地址', placeholder: '请输入注册地址' },
                  { field: 'officeAddress', label: '办公地址', placeholder: '请输入办公地址' },
                  { field: 'phone', label: '联系电话', placeholder: '请输入联系电话' },
                  { field: 'fax', label: '传真号码', placeholder: '请输入传真号码' },
                  { field: 'email', label: '电子邮箱', placeholder: '请输入电子邮箱' },
                  { field: 'bankName', label: '开户银行', placeholder: '请输入开户银行' },
                  { field: 'bankAccount', label: '银行账号', placeholder: '请输入银行账号' },
                  { field: 'taxId', label: '纳税人识别号', placeholder: '请输入纳税人识别号' },
                ].map(({ field, label, placeholder }) => (
                  <div key={field}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                    <input
                      type="text"
                      value={(formData as any)[field] || ''}
                      onChange={(e) => handleInputChange(field as keyof CompanyInfo, e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
                      placeholder={placeholder}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowForm(false);
                  setEditingCompany(null);
                  setFormData({ role: activeTab });
                }}
                className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 text-sm"
              >
                取消
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 rounded-lg bg-primary text-white hover:bg-primary-dark text-sm"
              >
                保存
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 公司列表 */}
      {visibleCompanies.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center border border-gray-100">
          <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-600 mb-2">
            {activeTab === 'partyA' ? '暂无我方公司信息' : '暂无乙方公司信息'}
          </h3>
          <p className="text-gray-400 mb-6">
            {activeTab === 'partyA' ? '添加您的第一个公司主体开始使用' : '管理员可在此添加常用的乙方公司信息'}
          </p>
          <button
            onClick={() => {
              setEditingCompany(null);
              setFormData({ role: activeTab });
              setShowForm(true);
            }}
            className="inline-flex items-center gap-2 bg-primary text-white px-6 py-2.5 rounded-lg hover:bg-primary-dark transition-colors"
          >
            <Plus className="w-4 h-4" />
            {activeTab === 'partyA' ? '添加公司' : '添加乙方公司'}
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 text-left text-sm text-gray-600">
                <tr>
                  <th className="px-6 py-3 font-medium">公司全称</th>
                  <th className="px-6 py-3 font-medium">统一社会信用代码</th>
                  <th className="px-6 py-3 font-medium">法定代表人</th>
                  <th className="px-6 py-3 font-medium">联系电话</th>
                  <th className="px-6 py-3 font-medium">角色</th>
                  <th className="px-6 py-3 font-medium text-right">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {visibleCompanies.map((company) => (
                  <tr key={company.id} className="hover:bg-gray-50/50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {company.createdBy === 'admin' && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700">
                            <Lock className="w-3 h-3" />
                            管理员添加
                          </span>
                        )}
                        {company.createdBy === 'system' && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-50 text-gray-500">
                            系统
                          </span>
                        )}
                        {company.createdBy === 'user' && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                            员工添加
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-400">{company.bankName}</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{company.creditCode || '-'}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{company.legalRepresentative || '-'}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{company.phone || '-'}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                        company.role === 'partyA'
                          ? 'bg-blue-50 text-blue-700'
                          : company.role === 'partyB'
                          ? 'bg-orange-50 text-orange-700'
                          : 'bg-purple-50 text-purple-700'
                      }`}>
                        {company.role === 'partyA' ? '甲方' : company.role === 'partyB' ? '乙方' : '通用'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        {canEditOrDelete(company) && (
                          <button
                            onClick={() => handleEdit(company)}
                            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500"
                            title="编辑"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                        )}
                        {canEditOrDelete(company) && (
                          <button
                            onClick={() => handleDelete(company.id, company.fullName)}
                            className="p-1.5 rounded-lg hover:bg-red-50 text-gray-500 hover:text-red-600"
                            title="删除"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                        {!canEditOrDelete(company) && (
                          <span className="text-xs text-gray-400">仅查看</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 危险操作区 */}
      {visibleCompanies.length > 0 && (
        <div className="bg-white rounded-xl p-4 border border-red-100">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-bold text-red-600">危险操作</h3>
              <p className="text-xs text-gray-500 mt-1">清除当前列表中的所有公司信息，此操作不可撤销</p>
            </div>
            <button
              onClick={handleClearAll}
              className="px-4 py-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 text-sm font-medium"
            >
              清除当前列表
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
